import { FC } from 'react';
import { Box, Stack, Typography, Tooltip, IconButton } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useTranslation } from 'react-i18next';
import { SCUIGeneralDrawerHeader } from '../../molecules';
import TimeHelper from '../../../utils/time';
import Swal from 'sweetalert2';

interface IProps {
    selectedRow?: any;
    onCloseMenu?: any;
}

const InfoRow: FC<{ label: string; value: string }> = ({ label, value }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            py: 1.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
        }}
    >
        <Typography
            variant="caption"
            sx={{
                color: 'text.disabled',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
            }}
        >
            {label}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary', wordBreak: 'break-word', lineHeight: 1.6 }}>
            {value}
        </Typography>
    </Box>
);

export const SCUIPaymentLogsDrawerContent: FC<IProps> = ({ selectedRow = null, onCloseMenu }) => {
    const { t } = useTranslation();

    let formattedData = selectedRow.data;
    try {
        formattedData = JSON.stringify(JSON.parse(selectedRow.data), null, 2);
    } catch {
        // not valid JSON, display as-is
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <SCUIGeneralDrawerHeader header={'Log Detay'} onClose={onCloseMenu} />

            <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
                <Stack spacing={0}>
                    <InfoRow label={t('general.customerId')} value={selectedRow.customerId} />
                    <InfoRow label={t('general.orderNo')} value={selectedRow.orderNumber} />
                    <InfoRow
                        label={`${t('general.date')} / ${t('general.hour')}`}
                        value={TimeHelper.formatDateHourSecond(selectedRow.dateTime)}
                    />
                    <InfoRow label={t('general.description')} value={selectedRow.description} />
                </Stack>

                <Box sx={{ mt: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1.5,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.disabled',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                            }}
                        >
                            {t('general.content')}
                        </Typography>
                        {selectedRow.data !== '-' && (
                            <Tooltip title={t('general.copyToClipboard')} placement="left">
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        navigator.clipboard.writeText(selectedRow.data);
                                        Swal.fire({
                                            icon: 'success',
                                            title: t('general.copied'),
                                            showConfirmButton: false,
                                            timer: 1500,
                                        });
                                    }}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        '&:hover': { backgroundColor: 'action.hover' },
                                    }}
                                >
                                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    <Box
                        component="pre"
                        sx={{
                            m: 0,
                            p: 2,
                            backgroundColor: '#1a1b26',
                            color: '#c0caf5',
                            borderRadius: 2,
                            fontSize: '0.72rem',
                            fontFamily: '"Fira Code", "Cascadia Code", "Consolas", monospace',
                            overflowX: 'auto',
                            overflowY: 'auto',
                            maxHeight: 400,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            lineHeight: 1.7,
                            border: '1px solid rgba(255,255,255,0.08)',
                            boxShadow: 'inset 0 1px 4px rgba(0,0,0,0.3)',
                        }}
                    >
                        {formattedData}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};
